// 店舗を選ぶ欄（複数選択＋候補の先頭に「全店舗」）の共通処理（2026-10-24）
// 棚卸・棚卸日登録・ユーザー登録の検索条件で使う

export interface ShopOption {
  code: string;
  name: string;
  code_name: string;
}

/** 「全店舗」の値（店舗コードと重ならない文字列） */
export const ALL_SHOPS = "__ALL__";

/** 候補の先頭に「全店舗」を足す */
export function withAllShops(shopItem: ShopOption[] | null | undefined, label: string): ShopOption[] {
  return [{ code: ALL_SHOPS, name: label, code_name: label }, ...(shopItem ?? [])];
}

/** 「全店舗」を選んでいるか */
export function isAllShops(value: string[] | null | undefined): boolean {
  return (value ?? []).includes(ALL_SHOPS);
}

/**
 * 選び直したときの調整。
 * 「全店舗」を選んだら他の店舗を外し、「全店舗」を選んでいるところで店舗を選んだら「全店舗」を外す。
 * 直さなくてよいときは value をそのまま返す（呼び出し側は === で比べて、違えば書き戻す）
 */
export function normalizeShopSelection(value: string[] | null | undefined, oldValue: string[] | null | undefined): string[] {
  const v = value ?? [];
  if (!v.includes(ALL_SHOPS) || v.length === 1) return v;
  return isAllShops(oldValue) ? v.filter((c) => c !== ALL_SHOPS) : [ALL_SHOPS];
}

/** 選んだ店舗コードのリスト。「全店舗」なら候補の店舗すべて */
export function selectedShopCodes(value: string[] | null | undefined, shopItem: ShopOption[] | null | undefined): string[] {
  if (isAllShops(value)) return (shopItem ?? []).map((e) => e.code);
  return (value ?? []).filter((c) => c !== ALL_SHOPS);
}

/** 店舗の候補として一度に描く上限（2026-10-26 修正指示書 S-32） */
export const SHOP_OPTION_LIMIT = 100;

/**
 * 店舗の欄に渡す候補を絞る（2026-10-26 修正指示書 S-32）。
 * Vuetify 3.1 の選択欄は仮想スクロールが無く、約700店舗を全部描くと開くたびにもたつくため、
 * 打った文字（店舗コード・店舗名）に当たるものを先頭から上限件数だけ渡す。
 * 先頭の「全店舗」と、選択済みの店舗（チップの表示に要る）は常に入れる
 */
export function limitShopOptions(options: ShopOption[], search: string | null | undefined,
    selected: string[] | null | undefined, limit = SHOP_OPTION_LIMIT): ShopOption[] {
  const q = (search ?? "").trim();
  const hits = (q ? options.filter((e) => e.code_name.includes(q)) : options).slice(0, limit);
  const codes = new Set(hits.map((e) => e.code));
  const keep = options.filter((e) => !codes.has(e.code)
    && (e.code === ALL_SHOPS || (selected ?? []).includes(e.code)));
  return [...keep.filter((e) => e.code === ALL_SHOPS), ...hits, ...keep.filter((e) => e.code !== ALL_SHOPS)];
}

/**
 * 店舗を選んだ直後も、打った文字（候補の絞り込み）を残すための見張り（2026-11-02、11/01 打合せの「チェックが勝手に付く」件）。
 * Vuetify 3.1 の複数選択は、選ぶたびに打った文字を消す（変える設定が無い）。すると候補が絞り込み前の並びに戻り、
 * マウスの下に別の行（先頭の「全店舗」など）が来るので、続けて押すとその行にチェックが付いていた。
 * 使い方：選択の更新で selected(値) を呼び、検索文字の更新は accepts(文字) が true のときだけ反映する
 */
export function createShopSearchGuard(): { selected: (value: string[] | null | undefined) => void; accepts: (search: string | null | undefined) => boolean } {
  let justSelected = false;
  return {
    // 選んだとき（空になったのはクリアなので見張らない）。Vuetify は選択→文字消しを同じ処理の中で続けて行う
    selected(value) {
      if (!value || value.length === 0) return;
      justSelected = true;
      Promise.resolve().then(() => { justSelected = false; });
    },
    accepts(search) {
      return !(justSelected && !search);
    },
  };
}
